import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { UTILS } from "../config/API";

const MyMessages = () => {
    // Navigation hook for programmatic routing
    const navigate = useNavigate();
    
    // State management
    const [messages, setMessages] = useState([]); // Stores all chat messages
    const [newMessage, setNewMessage] = useState(""); // Current message being typed
    const [loading, setLoading] = useState(true); // Loading state for initial load
    const [error, setError] = useState(null); // Error state for API failures
    const [fullName, setFullName] = useState(""); // Current user's full name
    
    // Refs for managing side effects
    const intervalRef = useRef(); // Stores the polling interval ID
    const initialLoadDone = useRef(false); // Tracks if initial load completed

    // Effect to load user's full name from session storage on component mount
    useEffect(() => {
        const storedFullName = sessionStorage.getItem("fullname");
        setFullName(storedFullName || "");
    }, []);

    /**
     * Formats date strings from API or Date objects into consistent display format
     * @param {string|Date} dateInput - The date to format (API string or Date object)
     * @returns {string} Formatted date string (MM/DD/YYYY HH:MM AM/PM)
     */
    const formatMessageDate = (dateInput) => {
        try {
            // Handle API date strings (e.g., "5/22/2025 2:11:07 PM")
            if (typeof dateInput === 'string') {
                // Remove seconds from timestamp (":07 PM" becomes " PM")
                return dateInput.replace(/(:\d{2})( [AP]M)/, '$2');
            }
            
            // Handle JavaScript Date objects (for new messages)
            const date = new Date(dateInput);
            const month = date.getMonth() + 1; // Months are 0-indexed
            const day = date.getDate();
            const year = date.getFullYear();
            let hours = date.getHours();
            const minutes = date.getMinutes().toString().padStart(2, '0'); // Ensure 2 digits
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12 || 12; // Convert to 12-hour format (0 becomes 12)

            return `${month}/${day}/${year} ${hours}:${minutes} ${ampm}`;
        } catch (error) {
            console.error("Error formatting date:", error);
            return "Just now"; // Fallback for formatting errors
        }
    };

    /**
     * Fetches messages from the API and updates state
     */
    const fetchMessages = async () => {
        try {
            const response = await fetch(UTILS, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "Accept": "application/json"
                },
                body: new URLSearchParams({
                    action: "messages", // API action parameter
                }),
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const data = await response.json();
            console.log(data)

            if (Array.isArray(data)) {
                // Transform API data into message objects
                const formattedMessages = data.map((msg, index) => ({
                    id: msg.id || Date.now() + index, // Fallback ID if missing
                    sender: msg.name,
                    text: msg.messagetext,
                    time: formatMessageDate(msg.messagedate), // Format timestamp
                    isMe: msg.name === fullName, // Check if message is from current user
                    avatar: msg.name.split(', ').map(part => part[0]).join('') // Create initials
                }));
                
                // Reverse to show newest at bottom and update state
                setMessages(formattedMessages.reverse());
            } 
            // else if (data?.status === "error") {
            //     handleLogout(); // Handle authentication errors
            // }
        } catch (error) {
            console.error("Error fetching messages:", error);
            setError(error.message); // Display error to user
        }
    };

    // Effect for initial data loading and polling setup
    useEffect(() => {
        if (fullName) {
            const initialLoad = async () => {
                setLoading(true);
                try {
                    await fetchMessages(); // Initial data fetch
                } finally {
                    setLoading(false);
                    // Scroll to bottom after initial load completes
                    setTimeout(() => {
                        const chatContainer = document.getElementById("chat-container");
                        if (chatContainer) {
                            chatContainer.scrollTop = chatContainer.scrollHeight;
                        }
                        initialLoadDone.current = true; // Mark initial load complete
                    }, 0);
                }
            };
            initialLoad();

            // Set up polling every 5 seconds (silent refreshes)
            intervalRef.current = setInterval(fetchMessages, 5000);
            
            // Cleanup: Clear interval when component unmounts
            return () => clearInterval(intervalRef.current);
        }
    }, [fullName]); // Re-run when fullName changes

    /**
     * Handles user logout by clearing session and redirecting
     */
    const handleLogout = () => {
        sessionStorage.removeItem("isAuthenticated");
        sessionStorage.removeItem("authToken");
        // sessionStorage.clear();
        navigate("/"); // Redirect to home page
    };

    /**
     * Handles sending a new message
     * @param {Event} e - Form submit event
     */
    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (newMessage.trim() === "") return; // Ignore empty messages

        // Create temporary message object (optimistic update)
        const tempId = Date.now();
        const newMsg = {
            id: tempId,
            sender: "You",
            text: newMessage,
            time: formatMessageDate(new Date()), // Format current time
            isMe: true
        };

        // Update state immediately (optimistic UI)
        setMessages(prev => [...prev, newMsg]);
        setNewMessage(""); // Clear input field

        // Scroll to bottom after state update
        setTimeout(() => {
            const chatContainer = document.getElementById("chat-container");
            if (chatContainer) {
                chatContainer.scrollTop = chatContainer.scrollHeight;
            }
        }, 0);

        try {
            // Send message to server
            const response = await fetch(UTILS, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "Accept": "application/json"
                },
                body: new URLSearchParams({
                    action: "sendmessage",
                    msg: newMessage
                }),
            });

            if (!response.ok) throw new Error('Failed to send message');
            
            const result = await response.json();
            if (result.status === "error") throw new Error(result.message);

            // Replace temporary ID with server ID if available
            setMessages(prev => prev.map(msg => 
                msg.id === tempId ? { ...msg, id: result.id || tempId } : msg
            ));

        } catch (error) {
            console.error("Error sending message:", error);
            // Rollback optimistic update on failure
            setMessages(prev => prev.filter(msg => msg.id !== tempId));
            setError("Failed to send message. Please try again.");
        }
    };

    // Component rendering
    return (
        <div className="flex flex-col h-screen bg-gray-50">
            {/* Fixed header at top */}
            <div className="fixed top-0 left-0 w-full z-50 bg-primary shadow-sm">
                <Header />
            </div>

            {/* Main content area with sidebar and messages */}
            <div className="flex flex-1 mt-[72px]">
                {/* Fixed sidebar (hidden on mobile) */}
                <div className="fixed left-0 w-64 bg-white h-[calc(100vh-72px)] overflow-y-auto hidden lg:block border-r border-gray-200">
                    <Sidebar />
                </div>

                {/* Scrollable message area */}
                <main className="flex-1 p-6 overflow-y-auto mt-[25px] lg:ml-64">
                    <div className="w-full md:w-[80%] mx-auto bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex flex-col" style={{ height: 'calc(100vh - 180px)' }}>
                        {/* Messages container */}
                        <div 
                            id="chat-container"
                            className="flex-1 overflow-y-auto mb-6 space-y-3 px-2"
                        >
                            {/* Error state */}
                            {error ? (
                                <div className="flex justify-center items-center h-full text-red-500">
                                    Error: {error}
                                </div>
                            ) : 
                            /* Loading state (only shows on initial load) */
                            loading ? (
                                <div className="flex justify-center items-center h-full">
                                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                                </div>
                            ) : 
                            /* Empty state */
                            messages.length === 0 ? (
                                <div className="flex justify-center items-center h-full text-gray-500">
                                    No messages found
                                </div>
                            ) : 
                            /* Messages list */
                            messages.map((message) => (
                                <div 
                                    key={message.id} 
                                    className={`flex ${message.isMe ? 'justify-end' : 'justify-start'} items-end gap-2`}
                                >
                                    {/* Avatar for received messages */}
                                    {!message.isMe && message.avatar && (
                                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-medium text-sm mb-1 flex-shrink-0">
                                            {message.avatar}
                                        </div>
                                    )}
                                    {/* Message bubble */}
                                    <div
                                        className={`max-w-[75%] rounded-lg p-3 ${message.isMe ? 'bg-primary text-white' : 'bg-gray-100 text-gray-800'}`}
                                    >
                                        {/* Sender name for received messages */}
                                        {!message.isMe ? (
                                            <div className="font-medium text-sm mb-1 text-primary-600">{message.sender}</div>
                                        ) : (
                                            /* "Me" label for sent messages */
                                            <div className="font-medium text-sm mb-1 text-primary-100">Me</div>
                                        )}
                                        {/* Message text */}
                                        <p className={message.isMe ? "text-white" : "text-gray-700"}>{message.text}</p>
                                        {/* Timestamp */}
                                        <div className={`text-xs mt-1 ${message.isMe ? 'text-primary-100' : 'text-gray-500'}`}>
                                            {message.time}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Message input form */}
                        <form onSubmit={handleSendMessage} className="border-t border-gray-200 pt-4">
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    placeholder="Type your message..."
                                    className="flex-1 border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-transparent text-gray-700 placeholder-gray-400"
                                />
                                <button
                                    type="submit"
                                    className="bg-primary hover:bg-primary-700 text-white py-3 px-6 rounded-lg transition duration-200 flex items-center justify-center"
                                >
                                    {/* Send icon */}
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </div>
                        </form>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default MyMessages;